import type { ReactNode } from 'react'

export type ImageInfo = {
  url: string
  content_type: string | null
  is_pdf: boolean
}

type SmokingState =
  | { state: 'none' }
  | { state: 'past'; age_from: number | null; age_to: number | null; number: number | null }
  | { state: 'present'; age_from: number | null; number: number | null }

type CommonInterview = {
  patient_insurance_card_image: ImageInfo | null
  recipient_certificate_image: ImageInfo | null
  medicine_notebook_image: ImageInfo | null
  other_useful_image: ImageInfo | null
  height: number | null
  weight: number | null
  basal_body_temperature: number | null
  is_possibility_of_pregnancy: string | null
  medical_history_select: string[]
  medical_history: string | null
  allergy_select: string[]
  allergy: string | null
  smoking: SmokingState
  is_current_medicine: string | null
  current_medicine: string | null
  is_side_effect: string | null
  side_effect: string | null
  generic_text: string | null
  is_current_supplement: string | null
  current_supplement: string | null
  is_try_medical_supplement: string | null
  visit_purpose: string[]
  desired_medicine: string | null
  symptom: string | null
  from_when: string | null
  other_information: string | null
}

type PmsExtras = {
  is_first_visit: string | null
  latest_menstruation_date: string | null
  latest_menstruation_term: number | null
  is_pills_before: string | null
  pills_medicine_name: string | null
  is_pill_prescription_desired: string | null
  pill_prescription_consent: boolean | null
  blood_pressure: string | null
  blood_pressure_max: string | null
  blood_pressure_min: string | null
  is_disease: string | null
  is_serious_disease_before: string | null
  pills_prescription_image: ImageInfo | null
}

type HerpesExtras = {
  site_of_onset_select: string[]
  site_of_onset: string | null
  first_diagnosis_date: string | null
}

type HyperhidrosisExtras = {
  site_of_onset_select: string[]
  site_of_onset: string | null
}

type InsomniaExtras = {
  somnus_email: string | null
  is_somnus_access_allowed: boolean | null
}

type Covid19Extras = {
  surrounding_infection_status: string[]
  is_insurance_claim_planned: boolean | null
  is_antiviral_desired: boolean | null
}

type ObesityExtras = {
  problems_caused_by_weight: string | null
  max_lifetime_weight: number | null
  age20_weight: number | null
  target_weight: number | null
  abdominal_circumference: number | null
  is_weight_loss_attempt: string | null
  eating_habit_select: string[]
  eating_habit: string | null
  weekly_exercise_time: string | null
  campaign_code: string | null
}

export type InterviewData = {
  type: string
  common: CommonInterview
  extras:
    | PmsExtras
    | HerpesExtras
    | HyperhidrosisExtras
    | InsomniaExtras
    | Covid19Extras
    | ObesityExtras
    | Record<string, never>
}

type OnImageClick = (image: ImageInfo) => void

type Props = {
  data: InterviewData
  onImageClick: OnImageClick
}

const labelClass = 'text-[13px] font-medium text-[#6f7680] mb-1'
const valueClass = 'text-[14px] text-[#2a3039] mb-4 whitespace-pre-wrap'
const listClass = 'text-[14px] text-[#2a3039] mb-4 space-y-0.5'

function LabeledValue({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className={labelClass}>{label}</div>
      <div className={valueClass}>{children}</div>
    </>
  )
}

function ImageField({ label, image, onImageClick, emptyLabel }: {
  label: ReactNode
  image: ImageInfo | null
  onImageClick: OnImageClick
  emptyLabel?: string
}) {
  if (!image) {
    return (
      <div className="mb-4">
        <div className={labelClass}>{emptyLabel ?? label}</div>
        <div className="text-[13px] text-[#b8bec7]">未選択</div>
      </div>
    )
  }
  return (
    <div className="mb-4">
      <div className="text-[13px] font-medium text-[#6f7680] mb-1 flex items-baseline gap-2">
        {label}
        <span className="text-[11px] font-normal text-[#97979a]">タップで拡大</span>
      </div>
      {image.is_pdf ? (
        <button
          type="button"
          onClick={() => onImageClick(image)}
          className="inline-block p-0 border border-[#e4e4e9] rounded-[6px] overflow-hidden bg-white cursor-pointer"
          aria-label="PDFを拡大表示"
        >
          <object data={image.url} type="application/pdf" width={100} height={100} style={{ pointerEvents: 'none' }} />
        </button>
      ) : (
        <img
          src={image.url}
          alt=""
          width={100}
          height={100}
          className="cursor-pointer rounded-[6px] border border-[#e4e4e9] object-cover"
          onClick={() => onImageClick(image)}
        />
      )}
    </div>
  )
}

function SelectedList({ label, items, otherDetail, otherLabel = 'その他', suffixFor }: {
  label: ReactNode
  items: string[]
  otherDetail?: string | null
  otherLabel?: string
  suffixFor?: (item: string) => string | null | undefined
}) {
  return (
    <>
      <div className={labelClass}>{label}</div>
      <div className={listClass}>
        {items.map((item, i) => {
          const suffix = suffixFor?.(item)
          if (item === otherLabel && otherDetail) {
            return <p key={i}>{item} （{otherDetail}）</p>
          }
          if (suffix) {
            return <p key={i}>{item}<br />{suffix}</p>
          }
          return <p key={i}>{item}</p>
        })}
      </div>
    </>
  )
}

function SmokingRow({ label, smoking }: { label: string; smoking: SmokingState }) {
  if (smoking.state === 'past') {
    return (
      <>
        <div className={labelClass}>{label}</div>
        <div className={valueClass}>
          過去に吸っていた （{smoking.age_from}~{smoking.age_to}歳まで、1日{smoking.number}本）
        </div>
      </>
    )
  }
  if (smoking.state === 'present') {
    return (
      <>
        <div className={labelClass}>{label}</div>
        <div className={valueClass}>
          吸っている （{smoking.age_from}歳から、1日{smoking.number}本）
        </div>
      </>
    )
  }
  return (
    <>
      <div className={labelClass}>{label}</div>
      <div className={valueClass}>吸わない</div>
    </>
  )
}

function MedicineWithDetail({ label, state, detail }: { label: string; state: string | null; detail: string | null }) {
  return (
    <>
      <div className={labelClass}>{label}</div>
      <div className={valueClass}>
        {state}
        {state === 'あり' && detail && (
          <>
            <br />
            {detail}
          </>
        )}
      </div>
    </>
  )
}

function VisitPurposeList({ items, desiredMedicine }: { items: string[]; desiredMedicine: string | null }) {
  return (
    <SelectedList
      label="診療の目的"
      items={items}
      suffixFor={(item) => (item === '処方薬がほしい（希望するお薬がある）' && desiredMedicine ? desiredMedicine : null)}
    />
  )
}

// 標準レイアウトの共通ヘッダ（画像群 + 身体情報 + 基本問診）
// 汎用フォーマット（Normal, Herpes, Hyperhidrosis, Insomnia, Covid19, Hypertension, Asthma）で使用
function StandardInterviewHeader({ c, onImageClick, includeRecipientCertificate = true }: {
  c: CommonInterview
  onImageClick: OnImageClick
  includeRecipientCertificate?: boolean
}) {
  return (
    <>
      <ImageField label="医療保険資格情報または資格確認書" image={c.patient_insurance_card_image} onImageClick={onImageClick} />
      {includeRecipientCertificate && (
        <ImageField label="医療費の助成資格" image={c.recipient_certificate_image} onImageClick={onImageClick} />
      )}
      <ImageField label="お薬手帳" image={c.medicine_notebook_image} onImageClick={onImageClick} />
      <ImageField label="診察の質を向上させるための参考画像" image={c.other_useful_image} onImageClick={onImageClick} />

      <LabeledValue label="身長（cm）">{c.height}</LabeledValue>
      <LabeledValue label="体重（kg）">{c.weight}</LabeledValue>
      <LabeledValue label="体温（℃）">{c.basal_body_temperature}</LabeledValue>
      {c.is_possibility_of_pregnancy && <LabeledValue label="妊娠の可能性">{c.is_possibility_of_pregnancy}</LabeledValue>}

      <SelectedList label="治療中または既往歴" items={c.medical_history_select} otherDetail={c.medical_history} />
      <SelectedList label="アレルギー" items={c.allergy_select} otherDetail={c.allergy} />
      <SmokingRow label="喫煙歴" smoking={c.smoking} />
      <MedicineWithDetail label="服用中のお薬" state={c.is_current_medicine} detail={c.current_medicine} />
      <MedicineWithDetail label="お薬の副作用" state={c.is_side_effect} detail={c.side_effect} />
      <LabeledValue label="ジェネリック医薬品の希望">{c.generic_text}</LabeledValue>
      <MedicineWithDetail label="飲んでいるサプリメント" state={c.is_current_supplement} detail={c.current_supplement} />
      <LabeledValue label="医療用サプリメントを試してみたいか">{c.is_try_medical_supplement}</LabeledValue>
      <VisitPurposeList items={c.visit_purpose} desiredMedicine={c.desired_medicine} />
    </>
  )
}

// 標準レイアウトの共通フッタ（症状・要望）
function StandardInterviewFooter({ c }: { c: CommonInterview }) {
  return (
    <>
      <LabeledValue label="症状の詳細">{c.symptom}</LabeledValue>
      <LabeledValue label="症状の発症時期">{c.from_when}</LabeledValue>
      <LabeledValue label="医師へ伝えたいことや質問したいこと">{c.other_information}</LabeledValue>
    </>
  )
}

// PMS と Obesity は並び・ラベルが大幅に違うため個別関数で維持
function PmsInterview({ c, e, onImageClick }: { c: CommonInterview; e: PmsExtras; onImageClick: OnImageClick }) {
  return (
    <>
      <LabeledValue label="初診ですか？">{e.is_first_visit === 'yes' ? 'はい' : 'いいえ'}</LabeledValue>
      <LabeledValue label="月経中/月経前後の症状を教えてください">{c.symptom}</LabeledValue>
      <LabeledValue label="その症状が出始めたのはいつ頃からですか?">{c.from_when}</LabeledValue>

      <ImageField label="医療保険資格情報または資格確認書" image={c.patient_insurance_card_image} onImageClick={onImageClick} />
      <ImageField label="助成資格画像" image={c.recipient_certificate_image} onImageClick={onImageClick} />

      <LabeledValue label="身長（cm）を入力してください">{c.height}</LabeledValue>
      <LabeledValue label="体重（kg）を入力してください">{c.weight}</LabeledValue>

      <LabeledValue label="最後に月経があったのはいつですか？">{e.latest_menstruation_date}</LabeledValue>
      {e.latest_menstruation_term != null && (
        <LabeledValue label="最後の月経日数">{e.latest_menstruation_term}日間</LabeledValue>
      )}

      <LabeledValue label="現在妊娠している、またはその可能性はありますか？">{c.is_possibility_of_pregnancy}</LabeledValue>

      <SelectedList label="治療中、あるいは今までに病気や手術したことはありますか？" items={c.medical_history_select} otherDetail={c.medical_history} />

      <div className={labelClass}>以下の疾患に該当するものはありますか？</div>
      <div className="oc-info-card text-[12px] leading-relaxed text-[#2a3039] mb-2">
        肥満・過敏性素因・乳癌・子宮癌・子宮筋腫・子宮腺筋症・子宮内膜症・チョコレート嚢腫・奇形腫・深部静脈血栓症・血栓性静脈炎・肺塞栓症・脳血管障害・冠動脈疾患・片頭痛・高血圧症・心疾患・糖尿病・血栓性素因・自己免疫疾患・肝障害・肝腫瘍・高脂血症・妊娠時黄疸・妊娠時持続性そう痒症・妊娠ヘルペス・ポルフィリン症・てんかん・テタニー・クローン病・腎疾患・潰瘍性大腸炎
      </div>
      <div className={valueClass}>{e.is_disease}</div>

      {e.blood_pressure && <LabeledValue label="血圧を教えてください">{e.blood_pressure}</LabeledValue>}
      <LabeledValue label="2回目以降のピル処方を希望の方は、測定結果を入力してください">
        {e.blood_pressure_max} / {e.blood_pressure_min}
      </LabeledValue>
      <LabeledValue label="ご家族に血栓症・乳がんにかかったことのある方はいますか？">{e.is_serious_disease_before}</LabeledValue>

      <SelectedList label="アレルギーはありますか？" items={c.allergy_select} otherDetail={c.allergy} />
      <SmokingRow label="タバコは吸いますか？" smoking={c.smoking} />
      <MedicineWithDetail label="現在飲んでいるお薬はありますか？" state={c.is_current_medicine} detail={c.current_medicine} />
      <MedicineWithDetail label="今までに低用量ピルまたはアフターピルを服用したことはありますか？" state={e.is_pills_before} detail={e.pills_medicine_name} />

      <ImageField label="処方されたお薬手帳画像" image={e.pills_prescription_image} onImageClick={onImageClick} />
      <ImageField label="最新のお薬手帳画像" image={c.medicine_notebook_image} onImageClick={onImageClick} />

      <MedicineWithDetail label="お薬の副作用が起きたことはありますか？" state={c.is_side_effect} detail={c.side_effect} />
      <LabeledValue label="ジェネリック医薬品を希望しますか？">{c.generic_text}</LabeledValue>
      <MedicineWithDetail label="現在飲んでいるサプリメントはありますか？" state={c.is_current_supplement} detail={c.current_supplement} />

      <LabeledValue label="医師に伝えたいことや質問がありますか？">{c.other_information}</LabeledValue>
      <ImageField label="診察の質を向上させるための参考画像" image={c.other_useful_image} onImageClick={onImageClick} />
    </>
  )
}

function ObesityInterview({ c, e, onImageClick }: { c: CommonInterview; e: ObesityExtras; onImageClick: OnImageClick }) {
  return (
    <>
      <ImageField label="医療保険資格情報または資格確認書" image={c.patient_insurance_card_image} onImageClick={onImageClick} />
      <ImageField label="お薬手帳" image={c.medicine_notebook_image} onImageClick={onImageClick} />
      <ImageField label="診察の質を向上させるための参考画像" image={c.other_useful_image} onImageClick={onImageClick} />

      <LabeledValue label="身長（cm）">{c.height}</LabeledValue>
      <LabeledValue label="体重（kg）">{c.weight}</LabeledValue>
      <LabeledValue label="体温（℃）">{c.basal_body_temperature}</LabeledValue>
      {c.is_possibility_of_pregnancy && <LabeledValue label="妊娠の可能性">{c.is_possibility_of_pregnancy}</LabeledValue>}

      <SelectedList label="治療中または既往歴" items={c.medical_history_select} otherDetail={c.medical_history} />
      <SelectedList label="アレルギー" items={c.allergy_select} otherDetail={c.allergy} />
      <SmokingRow label="喫煙歴" smoking={c.smoking} />
      <MedicineWithDetail label="服用中のお薬" state={c.is_current_medicine} detail={c.current_medicine} />
      <MedicineWithDetail label="お薬の副作用" state={c.is_side_effect} detail={c.side_effect} />
      <MedicineWithDetail label="飲んでいるサプリメント" state={c.is_current_supplement} detail={c.current_supplement} />
      <LabeledValue label="医療用サプリメントを試してみたいか">{c.is_try_medical_supplement}</LabeledValue>
      <VisitPurposeList items={c.visit_purpose} desiredMedicine={c.desired_medicine} />

      <LabeledValue label="体重が原因で困っていることや、不安なこと">{e.problems_caused_by_weight}</LabeledValue>
      <LabeledValue label="過去の最大体重">{e.max_lifetime_weight} kg</LabeledValue>
      <LabeledValue label="20歳頃の体重">{e.age20_weight} kg</LabeledValue>
      <LabeledValue label="目標体重">{e.target_weight} kg</LabeledValue>
      <LabeledValue label="腹囲">{e.abdominal_circumference} cm</LabeledValue>
      <LabeledValue label="減量に取り組んだ経験">{e.is_weight_loss_attempt}</LabeledValue>
      <SelectedList label="食事習慣" items={e.eating_habit_select} otherDetail={e.eating_habit} />
      <LabeledValue label="1週間の運動時間">{e.weekly_exercise_time}</LabeledValue>

      <LabeledValue label="医師へ伝えたいことや質問したいこと">{c.other_information}</LabeledValue>
      {e.campaign_code && <LabeledValue label="キャンペーンコード">{e.campaign_code}</LabeledValue>}
    </>
  )
}

export default function InterviewContent({ data, onImageClick }: Props) {
  const { type, common: c, extras } = data

  let content: ReactNode
  switch (type) {
    case 'pms_interview':
      content = <PmsInterview c={c} e={extras as PmsExtras} onImageClick={onImageClick} />
      break
    case 'obesity_interview':
      content = <ObesityInterview c={c} e={extras as ObesityExtras} onImageClick={onImageClick} />
      break
    case 'herpes_interview': {
      const e = extras as HerpesExtras
      content = (
        <>
          <StandardInterviewHeader c={c} onImageClick={onImageClick} />
          <SelectedList label="発症部位" items={e.site_of_onset_select} otherDetail={e.site_of_onset} />
          <LabeledValue label="はじめて診断された時期や年齢">{e.first_diagnosis_date}</LabeledValue>
          <StandardInterviewFooter c={c} />
        </>
      )
      break
    }
    case 'hyperhidrosis_interview': {
      const e = extras as HyperhidrosisExtras
      content = (
        <>
          <StandardInterviewHeader c={c} onImageClick={onImageClick} />
          <SelectedList label="発症部位" items={e.site_of_onset_select} otherDetail={e.site_of_onset} />
          <StandardInterviewFooter c={c} />
        </>
      )
      break
    }
    case 'insomnia_interview': {
      const e = extras as InsomniaExtras
      content = (
        <>
          <StandardInterviewHeader c={c} onImageClick={onImageClick} />
          <LabeledValue label="症状の詳細">{c.symptom}</LabeledValue>
          <LabeledValue label="症状の発症時期">{c.from_when}</LabeledValue>
          {e.somnus_email && <LabeledValue label="ソムナス登録メールアドレス">{e.somnus_email}</LabeledValue>}
          {e.is_somnus_access_allowed !== null && (
            <LabeledValue label="ソムナスデータ閲覧許可">{e.is_somnus_access_allowed ? '許可する' : '許可しない'}</LabeledValue>
          )}
          <LabeledValue label="医師へ伝えたいことや質問したいこと">{c.other_information}</LabeledValue>
        </>
      )
      break
    }
    case 'covid19_interview': {
      const e = extras as Covid19Extras
      content = (
        <>
          <StandardInterviewHeader c={c} onImageClick={onImageClick} />
          <SelectedList label="家族や職場など身の回りの感染状況" items={e.surrounding_infection_status} />
          {e.is_insurance_claim_planned !== null && (
            <LabeledValue label="保険請求予定">{e.is_insurance_claim_planned ? 'あり' : 'なし'}</LabeledValue>
          )}
          {e.is_antiviral_desired !== null && (
            <LabeledValue label="抗ウイルス薬の処方希望">{e.is_antiviral_desired ? 'あり' : 'なし'}</LabeledValue>
          )}
          <StandardInterviewFooter c={c} />
        </>
      )
      break
    }
    // 漢方外来(chinese_medicine_interview)、ピル外来(kagayaku_interview)、その他(default interview)は標準レイアウト
    default:
      content = (
        <>
          <StandardInterviewHeader c={c} onImageClick={onImageClick} />
          <StandardInterviewFooter c={c} />
        </>
      )
  }

  return <div>{content}</div>
}
